import React, { useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import MobileContext from 'Shared/contexts/MobileContext';
import Spinner from 'Shared/components/Spinner';
import { getRecurringMeetings } from 'Shared/actions/user';
import { getMembersAction } from 'Shared/actions/members';
import { getRecurringMeetingWithoutWorkspaces } from 'Shared/selectors/user';
import { getWorkspaceTeammatesIds } from 'Shared/selectors/workspace';
import { showNotificationError } from 'Shared/helpers';
import { useDeepEqualSelector } from 'Shared/utils/hooks';
import { getMembersWhoHaveNotTeammateYet } from 'Shared/selectors/members';
import SuggestedTeammateFromCalendar from '../../SuggestedTeammateFromCalendar/SuggestedTeammateFromCalendar';
import { getBaseTeammateSuggestionList } from '../../utils';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  includeOrgMembers: boolean;
  formValues: {};
  onSuggestedMeetingClick: Function;
};

const SUGGESTION_LENGTH_WEB = 4;
const SUGGESTION_LENGTH_MOBILE = 2;

function TeammateSuggestionsCalendarConnected({ includeOrgMembers, formValues, onSuggestedMeetingClick }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { tr } = useTranslation();
  const { id: currentUserId } = useCurrentUser();
  const members = useSelector(getMembersWhoHaveNotTeammateYet);

  const teammateIds = useDeepEqualSelector(getWorkspaceTeammatesIds);
  const recurringMeetingsWithoutWorkspaces = useDeepEqualSelector(getRecurringMeetingWithoutWorkspaces());
  const teammateSuggestionList = getBaseTeammateSuggestionList({
    includeOrgMembers,
    formValues,
    recurringMeetingsWithoutWorkspaces,
    currentUserId,
    teammateIds,
    members,
  });

  const isMobile = useContext(MobileContext);
  const suggestionsListLength = isMobile ? SUGGESTION_LENGTH_MOBILE : SUGGESTION_LENGTH_WEB;

  const renderList = teammateSuggestionList.splice(0, suggestionsListLength);

  useEffect(() => {
    async function fetchRecurringMeetings() {
      try {
        setIsLoading(true);
        await dispatch(getRecurringMeetings());
        await dispatch(getMembersAction());
      } catch (e) {
        showNotificationError(e);
      } finally {
        setIsLoading(false);
      }
    }

    fetchRecurringMeetings();
  }, []);

  if (!renderList.length) return null;

  return (
    <>
      <div className="add-teammate-sugestions-section-title pb-2">
        {tr('perform.simple_add_workspace.suggestions.teammate.connected.title')}
      </div>
      {isLoading ? (
        <div className="group-suggestions-calendar-connected-spinner w-100 p-4">
          <Spinner activeIf />
        </div>
      ) : (
        <div className="teammate-suggestions-calendar-connected scrollbar">
          {renderList.map(({ recurringMeeting, person, suggestionType }) => (
            <SuggestedTeammateFromCalendar
              key={person.email}
              recurringMeeting={recurringMeeting}
              person={person}
              suggestionType={suggestionType}
              onClick={onSuggestedMeetingClick}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default TeammateSuggestionsCalendarConnected;
